import Api from 'services/api'

import { CommentMapper } from './mappers'
import {
  AddReplyDto,
  CommentApiDto,
  CommentItem,
  CommentReply,
  CommentReplyDto,
  CreateCommentDto,
  FetchCommentsFilterParams,
  FetchCommentsResponse,
  UpdateCommentDto,
  UpdateCommentStatusDto
} from './types'

export const commentRepository = {
  getComments: async (
    buildingIdOrParams: number | string | FetchCommentsFilterParams,
    floorId?: number | string
  ): Promise<FetchCommentsResponse> => {
    let params: Record<string, any> = {}

    if (typeof buildingIdOrParams === 'object') {
      params = {
        building_id: buildingIdOrParams.buildingId,
        floor_id: buildingIdOrParams.floorId,
        point_id: buildingIdOrParams.pointId,
        sub_image_id:
          buildingIdOrParams.subImageId ?? buildingIdOrParams.sub_image_id,
        status: buildingIdOrParams.status
      }
    } else {
      params = {
        building_id: buildingIdOrParams,
        floor_id: floorId
      }
    }

    const res: any = await Api.get<any>('/v1/comments', params)

    const rawItems: CommentApiDto[] = Array.isArray(res) ? res : res?.data || []
    const items: CommentItem[] = rawItems
      .map((item) => CommentMapper.toDomainEntity(item))
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )

    const openCount =
      typeof res?.unresolved_count === 'number'
        ? res.unresolved_count
        : items.filter((c) => c.status === 'open').length

    if (typeof window !== 'undefined') {
      if (!(window as any).rnCommentsMap) {
        ;(window as any).rnCommentsMap = new Map()
      }
      const gMap: Map<string, any> = (window as any).rnCommentsMap
      items.forEach((item) => {
        if (item.annotationId) gMap.set(String(item.annotationId), item)
        if ((item as any).annotation_id)
          gMap.set(String((item as any).annotation_id), item)
        if (item.id) gMap.set(String(item.id), item)
      })
    }

    return {
      data: items,
      total: res?.total ?? items.length,
      unfilteredTotal: res?.unfiltered_total ?? res?.total ?? items.length,
      unresolvedCount: openCount,
      page: 1,
      perPage: items.length,
      totalPages: 1
    }
  },

  getCommentDetail: async (
    commentId: number | string
  ): Promise<CommentItem> => {
    const res = await Api.get<CommentApiDto>(`/v1/comments/${commentId}`)
    const item = CommentMapper.toDomainEntity(res)
    if (typeof window !== 'undefined') {
      if (!(window as any).rnCommentsMap) {
        ;(window as any).rnCommentsMap = new Map()
      }
      const gMap: Map<string, any> = (window as any).rnCommentsMap
      if (item.annotationId) gMap.set(String(item.annotationId), item)
      if (item.id) gMap.set(String(item.id), item)
    }
    return item
  },

  createComment: async (
    payload: CreateCommentDto | { data: CreateCommentDto }
  ) => {
    const data = (payload as any)?.data || payload
    const res: any = await Api.post('/v1/comments', data)
    const entity = CommentMapper.toDomainEntity(res?.data || res)
    if (typeof window !== 'undefined') {
      if (!(window as any).rnCommentsMap) {
        ;(window as any).rnCommentsMap = new Map()
      }
      const gMap: Map<string, any> = (window as any).rnCommentsMap
      if (entity.annotationId) gMap.set(String(entity.annotationId), entity)
      if (entity.id) gMap.set(String(entity.id), entity)
    }
    return entity
  },

  updateComment: async (
    commentId: number | string,
    payload: UpdateCommentDto | { data: UpdateCommentDto }
  ): Promise<CommentItem> => {
    const data = (payload as any)?.data || payload
    const res: any = await Api.put(`/v1/comments/${commentId}`, data)
    const entity = CommentMapper.toDomainEntity(res?.data || res)
    if (typeof window !== 'undefined') {
      if (!(window as any).rnCommentsMap) {
        ;(window as any).rnCommentsMap = new Map()
      }
      const gMap: Map<string, any> = (window as any).rnCommentsMap
      if (entity.annotationId) gMap.set(String(entity.annotationId), entity)
      if (entity.id) gMap.set(String(entity.id), entity)
    }
    return entity
  },

  updateStatus: async (
    payload: UpdateCommentStatusDto | { data: UpdateCommentStatusDto }
  ) => {
    const data = (payload as any)?.data || payload
    return Api.patch('/v1/comments/status', data)
  },

  addReply: async (
    commentId: number | string,
    payload: AddReplyDto | string
  ): Promise<CommentReply> => {
    const data = typeof payload === 'string' ? { body: payload } : payload
    const res: any = await Api.post(`/v1/comments/${commentId}/replies`, data)
    return CommentMapper.toReplyEntity(res?.data || res)
  },

  updateReply: async (
    replyId: number | string,
    payload: AddReplyDto | string
  ): Promise<CommentReply> => {
    const data = typeof payload === 'string' ? { body: payload } : payload
    const res: any = await Api.put(`/v1/comments/replies/${replyId}`, data)
    return CommentMapper.toReplyEntity(res?.data || res)
  },

  deleteReply: async (replyId: number | string) => {
    return Api.delete(`/v1/comments/replies/${replyId}`, {})
  },

  deleteComment: async (commentId: number | string) => {
    return Api.delete(`/v1/comments/${commentId}`, {})
  },

  getReplies: async (
    commentId: number | string,
    page: number = 1,
    perPage: number = 10
  ): Promise<{
    data: CommentReply[]
    total: number
    page: number
    perPage: number
    totalPages: number
  }> => {
    const res: any = await Api.get(`/v1/comments/${commentId}/replies`, {
      page,
      per_page: perPage
    })
    const rawItems: CommentReplyDto[] = Array.isArray(res)
      ? res
      : res?.data || []
    const items = rawItems.map((r) => CommentMapper.toReplyEntity(r))

    return {
      data: items,
      total: res?.total ?? items.length,
      page: res?.page ?? page,
      perPage: res?.per_page ?? perPage,
      totalPages: res?.total_pages ?? 1
    }
  }
}

export default commentRepository
